import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export type AlertTone = 'info' | 'warning' | 'critical' | 'good'

const TONE_STYLES: Record<AlertTone, { container: string; icon: string }> = {
  info: { container: 'border-accent-ring/40 bg-accent-soft', icon: 'text-accent' },
  warning: { container: 'border-warning/35 bg-warning-soft', icon: 'text-warning' },
  critical: { container: 'border-critical/35 bg-critical-soft', icon: 'text-critical' },
  good: { container: 'border-good/35 bg-good-soft', icon: 'text-good' },
}

const ICONS: Record<AlertTone, ReactNode> = {
  info: <path d="M8 7.5v4.5M8 4.5v.01" strokeWidth="1.75" strokeLinecap="round" />,
  warning: <path d="M8 5.5v3.5M8 11.5v.01" strokeWidth="1.75" strokeLinecap="round" />,
  critical: <path d="M8 5.5v3.5M8 11.5v.01" strokeWidth="1.75" strokeLinecap="round" />,
  good: <path d="M4.5 8.5l2.5 2.5 4.5-5" strokeWidth="1.75" strokeLinecap="round" />,
}

export interface AlertProps {
  tone?: AlertTone
  title: string
  children?: ReactNode
  actions?: ReactNode
  /** Raw technical text, folded away behind a disclosure. */
  details?: string
  className?: string
}

/**
 * Icon, title, one short body, then actions.
 *
 * The icon and the wording carry the meaning, so the tone colour is
 * reinforcement rather than the signal itself. Anything longer than a sentence
 * or two belongs in `details`, which stays closed until asked for.
 */
export function Alert({
  tone = 'info',
  title,
  children,
  actions,
  details,
  className,
}: AlertProps) {
  const styles = TONE_STYLES[tone]

  return (
    <div
      role={tone === 'critical' ? 'alert' : 'status'}
      className={cn('rounded-lg border p-3', styles.container, className)}
    >
      <div className="flex items-start gap-2.5">
        <svg
          viewBox="0 0 16 16"
          className={cn('mt-0.5 h-4 w-4 shrink-0', styles.icon)}
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <circle cx="8" cy="8" r="6.5" strokeWidth="1.25" opacity="0.45" />
          {ICONS[tone]}
        </svg>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-ink">{title}</p>
          {children ? (
            <div className="mt-1 text-sm text-ink-secondary">{children}</div>
          ) : null}
          {details ? <ErrorDetails text={details} /> : null}
          {actions ? (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">{actions}</div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** The raw text, available but never in the way. */
export function ErrorDetails({ text, className }: { text: string; className?: string }) {
  return (
    <details className={cn('group mt-2', className)}>
      {/* `secondary`, not `muted`: this is a control, and muted was tuned
          against the neutral grounds — on the tinted critical ground it lands
          at 4.36:1, just under the threshold. */}
      <summary className="inline-flex w-fit cursor-pointer items-center gap-1 text-xs text-ink-secondary transition-colors hover:text-ink">
        <svg
          viewBox="0 0 12 12"
          className="h-2.5 w-2.5 transition-transform duration-150 ease-out group-open:rotate-90"
          aria-hidden="true"
        >
          <path
            d="M4.5 3l3 3-3 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        <span className="group-open:hidden">Show technical details</span>
        <span className="hidden group-open:inline">Hide technical details</span>
      </summary>
      <pre className="scrollbar-slim mt-1.5 max-h-40 overflow-auto rounded-md border border-line bg-surface-sunken p-2.5 font-mono text-xs leading-relaxed break-words whitespace-pre-wrap text-ink-secondary">
        {text}
      </pre>
    </details>
  )
}
